import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  getPlayerProfile, getPlayerSummary, getPlayerCasinoGames,
  getPlayerSportBreakdown, getPlayerDailyActivity, getCashierTransactions,
} from '../api/client';
import { currencySymbol } from '../components/CurrencySelector';
import { formatAmount, formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { InfoTip } from '../components/InfoTip';
import { CurrencySelector } from '../components/CurrencySelector';
import type { CurrencyMode } from '../components/CurrencySelector';
import { siteName, useSites } from '../components/SiteFilter';
import { ArrowLeft, User, CreditCard, Gamepad2, Trophy, Activity, TrendingUp, TrendingDown } from 'lucide-react';

// ---------- types ----------

interface Profile {
  player_id: string; site_id: number; username: string;
  country_code: string; language_code: string; currency_code: string;
  registration_date: string; status: string; kyc_level: string;
  affiliate_id?: string; gender?: string; birth_date?: string;
  first_deposit_date?: string; first_deposit_amount?: number;
  last_login_at?: string; total_logins: number;
}

interface Summary {
  casino_bet_amount: number; casino_win_amount: number; casino_ggr: number;
  casino_rounds: number; casino_sessions: number; unique_games: number;
  sport_bet_amount: number; sport_win_amount: number; sport_ggr: number; sport_bets: number;
  total_deposits: number; total_withdrawals: number; net_cash: number;
  deposit_count: number; withdrawal_count: number;
  bonus_awarded: number; bonus_redeemed: number;
}

interface CasinoGame {
  game_id: number; game_name: string; game_type?: string;
  bet_amount: number; win_amount: number; ggr: number; rounds: number; rtp: number;
}

interface SportRow {
  sport: string; bet_amount: number; win_amount: number; ggr: number; bet_count: number;
}

interface DailyActivity {
  date: string; casino_bet: number; casino_win: number; casino_ggr: number; casino_rounds: number;
  sport_bet: number; sport_win: number; sport_ggr: number; deposits: number; withdrawals: number;
}

interface Transaction {
  event_date: string; event_type: string; player_id: string; site_id: number;
  payment_method: string; payment_method_type: string; amount: number; amount_eur: number;
  currency: string; movement_id: number; external_transaction_id: string; is_first_deposit: boolean;
}

type Tab = 'overview' | 'casino' | 'sport' | 'transactions' | 'activity';

// ---------- helpers ----------

function fmtMoney(sym: string, v: number): string {
  return `${sym}${formatAmount(v || 0)}`;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function defaultDateFrom(): string {
  const d = new Date(); d.setDate(d.getDate() - 90);
  return d.toISOString().split('T')[0];
}
function defaultDateTo(): string {
  return new Date().toISOString().split('T')[0];
}

// ---------- small components ----------

const Loading: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
  </div>
);

const ErrorMsg: React.FC<{ msg: string }> = ({ msg }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">{msg}</div>
);

const Badge: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>{label}</span>
);

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    suspended: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    blocked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    closed: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  };
  return <Badge label={status} color={colors[status?.toLowerCase()] || colors.active} />;
}

function kycBadge(level: string) {
  const colors: Record<string, string> = {
    verified: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    none: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  };
  return <Badge label={`KYC: ${level || 'none'}`} color={colors[level?.toLowerCase()] || colors.none} />;
}

// ---------- KPI Card ----------

const KPICard: React.FC<{ label: string; value: string; sub?: string; icon?: React.ReactNode; color?: string }> = ({ label, value, sub, icon, color }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-center gap-2">
      {icon && <span className={color || 'text-accent'}>{icon}</span>}
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</span>
    </div>
    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{sub}</p>}
  </div>
);

// ---------- Tab Nav ----------

const tabs: { key: Tab; icon: React.ReactNode; label: string }[] = [
  { key: 'overview', icon: <User size={16} />, label: 'Overview' },
  { key: 'casino', icon: <Gamepad2 size={16} />, label: 'Casino' },
  { key: 'sport', icon: <Trophy size={16} />, label: 'Sport' },
  { key: 'transactions', icon: <CreditCard size={16} />, label: 'Transactions' },
  { key: 'activity', icon: <Activity size={16} />, label: 'Activity' },
];

const TabNav: React.FC<{ active: Tab; onChange: (t: Tab) => void }> = ({ active, onChange }) => (
  <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800">
    {tabs.map(({ key, icon, label }) => (
      <button key={key} onClick={() => onChange(key)}
        className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors
          ${active === key ? 'bg-white text-accent shadow-sm dark:bg-slate-700 dark:text-accent' : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'}`}>
        {icon}{label}
      </button>
    ))}
  </div>
);

// ---------- Data Fetcher Hook ----------

function useFetch<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fn().then(res => { if (!cancelled) setData(res); })
      .catch(err => { if (!cancelled) setError(String(err?.message || err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

// ==================== MAIN PAGE ====================

export const PlayerDetail: React.FC = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const { allSites } = useSites();

  const playerId = params.get('player_id') || '';
  const siteId = parseInt(params.get('site_id') || '0', 10);
  const tab = (params.get('tab') || 'overview') as Tab;
  const dateFrom = params.get('date_from') || defaultDateFrom();
  const dateTo = params.get('date_to') || defaultDateTo();
  const currencyMode = (params.get('display_currency') || 'EUR') as CurrencyMode;

  const patch = useCallback((updates: Record<string, string | null>) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === '') next.delete(k); else next.set(k, v);
      }
      return next;
    });
  }, [setParams]);

  const setTab = useCallback((t: Tab) => patch({ tab: t === 'overview' ? null : t }), [patch]);

  const sym = currencySymbol(currencyMode);

  const baseParams = useMemo(() => ({
    player_id: playerId, site_id: siteId, date_from: dateFrom, date_to: dateTo, display_currency: currencyMode,
  }), [playerId, siteId, dateFrom, dateTo, currencyMode]);

  // Fetch profile (no date dependency)
  const { data: profileData, loading: profileLoading, error: profileError } = useFetch<{ data: Profile }>(
    () => getPlayerProfile({ player_id: playerId, site_id: siteId }),
    [playerId, siteId],
  );

  // Fetch summary
  const { data: summaryData, loading: summaryLoading } = useFetch<{ data: Summary }>(
    () => getPlayerSummary(baseParams),
    [playerId, siteId, dateFrom, dateTo, currencyMode],
  );

  if (!playerId || !siteId) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('playerDetail.title', { defaultValue: 'Player Detail' })}</h1>
        <ErrorMsg msg="Missing player_id or site_id in URL parameters" />
      </div>
    );
  }

  const profile = profileData?.data;
  const summary = summaryData?.data;

  return (
    <div className="space-y-6">
      {/* Back link + header */}
      <div className="flex items-center gap-4">
        <Link to="/reports/financial?view=players" className="rounded-lg border border-slate-200 p-2 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800">
          <ArrowLeft size={20} className="text-slate-600 dark:text-slate-400" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              {profileLoading ? playerId : (profile?.username || playerId)}
            </h1>
            {profile && statusBadge(profile.status)}
            {profile && kycBadge(profile.kyc_level)}
          </div>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            Player ID: {playerId} · Site: {siteName(allSites, siteId)} · Currency: {profile?.currency_code || '—'}
          </p>
        </div>
      </div>

      {profileError && <ErrorMsg msg={profileError} />}

      {/* Profile card */}
      {profile && (
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800 sm:grid-cols-3 lg:grid-cols-6">
          <div><span className="text-xs text-slate-500 dark:text-slate-400">Registered</span><p className="font-medium text-slate-900 dark:text-white">{fmtDate(profile.registration_date)}</p></div>
          <div><span className="text-xs text-slate-500 dark:text-slate-400">Country</span><p className="font-medium text-slate-900 dark:text-white">{profile.country_code || '—'}</p></div>
          <div><span className="text-xs text-slate-500 dark:text-slate-400">Language</span><p className="font-medium text-slate-900 dark:text-white">{profile.language_code || '—'}</p></div>
          <div><span className="text-xs text-slate-500 dark:text-slate-400">Affiliate</span><p className="font-medium text-slate-900 dark:text-white">{profile.affiliate_id || '—'}</p></div>
          <div><span className="text-xs text-slate-500 dark:text-slate-400">Last Login</span><p className="font-medium text-slate-900 dark:text-white">{profile.last_login_at ? fmtDateTime(profile.last_login_at) : '—'}</p></div>
          <div><span className="text-xs text-slate-500 dark:text-slate-400">Total Logins</span><p className="font-medium text-slate-900 dark:text-white">{formatNumber(profile.total_logins || 0)}</p></div>
        </div>
      )}

      {/* Date range + currency */}
      <div className="flex flex-wrap items-center gap-3">
        <input type="date" value={dateFrom} onChange={e => patch({ date_from: e.target.value })}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white" />
        <span className="text-slate-500">—</span>
        <input type="date" value={dateTo} onChange={e => patch({ date_to: e.target.value })}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800 dark:text-white" />
        <CurrencySelector selected={currencyMode} onChange={v => patch({ display_currency: v })} />
      </div>

      {/* KPI Summary */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <KPICard label="Casino GGR" value={fmtMoney(sym, summary.casino_ggr)} sub={`${formatNumber(summary.casino_rounds)} rounds`} icon={<Gamepad2 size={18} />} />
          <KPICard label="Sport GGR" value={fmtMoney(sym, summary.sport_ggr)} sub={`${formatNumber(summary.sport_bets)} bets`} icon={<Trophy size={18} />} />
          <KPICard label="Total Deposits" value={fmtMoney(sym, summary.total_deposits)} sub={`${summary.deposit_count} deposits`} icon={<TrendingUp size={18} />} color="text-green-600" />
          <KPICard label="Total Withdrawals" value={fmtMoney(sym, summary.total_withdrawals)} sub={`${summary.withdrawal_count} withdrawals`} icon={<TrendingDown size={18} />} color="text-red-500" />
          <KPICard label="Net Cash" value={fmtMoney(sym, summary.net_cash)} />
          <KPICard label="Unique Games" value={formatNumber(summary.unique_games)} sub={`${formatNumber(summary.casino_sessions)} sessions`} />
          <KPICard label="Casino Turnover" value={fmtMoney(sym, summary.casino_bet_amount)} />
          <KPICard label="Bonus Awarded" value={fmtMoney(sym, summary.bonus_awarded)} sub={`Redeemed: ${fmtMoney(sym, summary.bonus_redeemed)}`} />
        </div>
      )}
      {summaryLoading && <Loading />}

      {/* Sub-tabs */}
      <TabNav active={tab} onChange={setTab} />

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab baseParams={baseParams} sym={sym} />}
      {tab === 'casino' && <CasinoTab baseParams={baseParams} sym={sym} />}
      {tab === 'sport' && <SportTab baseParams={baseParams} sym={sym} />}
      {tab === 'transactions' && <TransactionsTab baseParams={baseParams} />}
      {tab === 'activity' && <ActivityTab baseParams={baseParams} sym={sym} />}
    </div>
  );
};

// ==================== OVERVIEW TAB ====================

const OverviewTab: React.FC<{ baseParams: { player_id: string; site_id: number; date_from: string; date_to: string; display_currency: string }; sym: string }> = ({ baseParams, sym }) => {
  const { data, loading, error } = useFetch<{ data: DailyActivity[] }>(
    () => getPlayerDailyActivity(baseParams),
    [baseParams.player_id, baseParams.site_id, baseParams.date_from, baseParams.date_to, baseParams.display_currency],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];
  if (rows.length === 0) return <p className="text-center text-slate-500 py-8">No activity in selected period</p>;

  return (
    <div className="space-y-6">
      {/* GGR trend chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Daily GGR Trend</h2>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '11px' }} />
            <YAxis stroke="#9ca3af" tickFormatter={(v: number) => fmtMoney(sym, v)} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number) => fmtMoney(sym, v)} />
            <Legend />
            <Bar dataKey="casino_ggr" name="Casino GGR" fill="#009EA0" radius={[2, 2, 0, 0]} />
            <Bar dataKey="sport_ggr" name="Sport GGR" fill="#f59e0b" radius={[2, 2, 0, 0]} />
            <Line dataKey="deposits" name="Deposits" type="monotone" stroke="#22c55e" strokeWidth={2} dot={false} />
            <Line dataKey="withdrawals" name="Withdrawals" type="monotone" stroke="#ef4444" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Daily activity table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: 'Date', i: 'date' }, { l: 'Casino Bet', i: 'totalBet' }, { l: 'Casino Win', i: 'totalWin' }, { l: 'Casino GGR', i: 'casinoGgr' }, { l: 'Rounds', i: 'rounds' }, { l: 'Sport Bet', i: 'totalBet' }, { l: 'Sport Win', i: 'totalWin' }, { l: 'Sport GGR', i: 'sportGgr' }, { l: 'Deposits', i: 'deposits' }, { l: 'Withdrawals', i: 'withdrawals' }].map(h => (
                <th key={h.l} className="px-3 py-2.5 font-medium text-slate-700 dark:text-slate-300"><InfoTip tip={metricTip(h.i)}>{h.l}</InfoTip></th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-3 py-2 text-slate-900 dark:text-white">{r.date}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.casino_bet)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.casino_win)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.casino_ggr)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{formatNumber(r.casino_rounds)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.sport_bet)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.sport_win)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.sport_ggr)}</td>
                <td className="px-3 py-2 text-green-600">{fmtMoney(sym, r.deposits)}</td>
                <td className="px-3 py-2 text-red-500">{fmtMoney(sym, r.withdrawals)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== CASINO TAB ====================

const GAME_TYPE_COLORS: Record<string, string> = {
  casino: '#009EA0', live: '#f59e0b', crash: '#ef4444', virtuals: '#8b5cf6',
};

const CasinoTab: React.FC<{ baseParams: { player_id: string; site_id: number; date_from: string; date_to: string; display_currency: string }; sym: string }> = ({ baseParams, sym }) => {
  const { data, loading, error } = useFetch<{ data: CasinoGame[] }>(
    () => getPlayerCasinoGames({ ...baseParams, limit: 100 }),
    [baseParams.player_id, baseParams.site_id, baseParams.date_from, baseParams.date_to, baseParams.display_currency],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];
  if (rows.length === 0) return <p className="text-center text-slate-500 py-8">No casino activity in selected period</p>;

  // Donut chart: GGR by game (top 10 + others)
  const sorted = [...rows].sort((a, b) => b.ggr - a.ggr);
  const top10 = sorted.slice(0, 10);
  const othersGgr = sorted.slice(10).reduce((sum, r) => sum + r.ggr, 0);
  const chartData = [...top10.map(r => ({ name: r.game_name || `Game ${r.game_id}`, value: r.ggr }))];
  if (othersGgr > 0) chartData.push({ name: 'Others', value: othersGgr });
  const COLORS = ['#009EA0', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6', '#22c55e', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#94a3b8'];

  return (
    <div className="space-y-6">
      {/* GGR by game donut */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">GGR by Game</h2>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={80} outerRadius={140} paddingAngle={2} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
              {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number) => fmtMoney(sym, v)} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Games table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: 'Game', i: 'game' }, { l: 'Type', i: 'gameType' }, { l: 'Bet Amount', i: 'totalBet' }, { l: 'Win Amount', i: 'totalWin' }, { l: 'GGR', i: 'ggr' }, { l: 'Rounds', i: 'rounds' }, { l: 'RTP %', i: 'rtp' }].map(h => (
                <th key={h.l} className="px-3 py-2.5 font-medium text-slate-700 dark:text-slate-300"><InfoTip tip={metricTip(h.i)}>{h.l}</InfoTip></th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.map((r, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{r.game_name || r.game_id}</td>
                <td className="px-3 py-2">
                  {r.game_type && <span className="rounded px-1.5 py-0.5 text-xs font-medium" style={{ backgroundColor: (GAME_TYPE_COLORS[r.game_type] || '#94a3b8') + '20', color: GAME_TYPE_COLORS[r.game_type] || '#94a3b8' }}>{r.game_type}</span>}
                </td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.bet_amount)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, r.win_amount)}</td>
                <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{fmtMoney(sym, r.ggr)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{formatNumber(r.rounds)}</td>
                <td className="px-3 py-2 text-slate-900 dark:text-white">{formatNumber((r.rtp || 0) * 100, 2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== SPORT TAB ====================

const SportTab: React.FC<{ baseParams: { player_id: string; site_id: number; date_from: string; date_to: string; display_currency: string }; sym: string }> = ({ baseParams, sym }) => {
  const { data, loading, error } = useFetch<{ data: SportRow[] }>(
    () => getPlayerSportBreakdown(baseParams),
    [baseParams.player_id, baseParams.site_id, baseParams.date_from, baseParams.date_to, baseParams.display_currency],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];
  if (rows.length === 0) return <p className="text-center text-slate-500 py-8">No sportsbook activity in selected period</p>;

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800">
          <tr>
            {[{ l: 'Sport', i: '' }, { l: 'Bet Amount', i: 'totalBet' }, { l: 'Win Amount', i: 'totalWin' }, { l: 'GGR', i: 'ggr' }, { l: 'Bets', i: 'betCount' }].map(h => (
              <th key={h.l} className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300"><InfoTip tip={metricTip(h.i)}>{h.l}</InfoTip></th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {rows.map((r, i) => (
            <tr key={i} className="bg-white dark:bg-slate-900">
              <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{r.sport}</td>
              <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, r.bet_amount)}</td>
              <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, r.win_amount)}</td>
              <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{fmtMoney(sym, r.ggr)}</td>
              <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(r.bet_count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==================== TRANSACTIONS TAB ====================

const TransactionsTab: React.FC<{ baseParams: { player_id: string; site_id: number; date_from: string; date_to: string; display_currency: string } }> = ({ baseParams }) => {
  const { data, loading, error } = useFetch<{ data: Transaction[] }>(
    () => getCashierTransactions({
      date_from: baseParams.date_from,
      date_to: baseParams.date_to,
      site_ids: String(baseParams.site_id),
      player_id: baseParams.player_id,
      limit: 200,
    }),
    [baseParams.player_id, baseParams.site_id, baseParams.date_from, baseParams.date_to],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];
  if (rows.length === 0) return <p className="text-center text-slate-500 py-8">No transactions in selected period</p>;

  const eventColors: Record<string, string> = {
    deposit_completed: 'text-green-600',
    withdrawal_paid: 'text-red-500',
    withdrawal_cancelled: 'text-yellow-600',
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800">
          <tr>
            {[{ l: 'Date', i: 'date' }, { l: 'Type', i: '' }, { l: 'Amount', i: '' }, { l: 'Currency', i: 'currency' }, { l: 'Payment Method', i: 'paymentMethod' }, { l: 'Method Type', i: 'paymentType' }, { l: 'FTD', i: 'ftdCount' }, { l: 'Movement ID', i: '' }].map(h => (
              <th key={h.l} className="px-3 py-2.5 font-medium text-slate-700 dark:text-slate-300"><InfoTip tip={metricTip(h.i)}>{h.l}</InfoTip></th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {rows.map((r, i) => (
            <tr key={i} className="bg-white dark:bg-slate-900">
              <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtDateTime(r.event_date)}</td>
              <td className={`px-3 py-2 font-medium ${eventColors[r.event_type] || 'text-slate-900 dark:text-white'}`}>
                {r.event_type.replace(/_/g, ' ')}
              </td>
              <td className="px-3 py-2 text-slate-900 dark:text-white">{formatAmount(r.amount || 0)}</td>
              <td className="px-3 py-2 text-slate-900 dark:text-white">{r.currency}</td>
              <td className="px-3 py-2 text-slate-900 dark:text-white">{r.payment_method}</td>
              <td className="px-3 py-2 text-slate-900 dark:text-white">{r.payment_method_type}</td>
              <td className="px-3 py-2">{r.is_first_deposit ? <Badge label="FTD" color="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300" /> : ''}</td>
              <td className="px-3 py-2 text-slate-500 dark:text-slate-400 font-mono text-xs">{r.movement_id || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==================== ACTIVITY TAB ====================

const ActivityTab: React.FC<{ baseParams: { player_id: string; site_id: number; date_from: string; date_to: string; display_currency: string }; sym: string }> = ({ baseParams, sym }) => {
  const { data, loading, error } = useFetch<{ data: DailyActivity[] }>(
    () => getPlayerDailyActivity(baseParams),
    [baseParams.player_id, baseParams.site_id, baseParams.date_from, baseParams.date_to, baseParams.display_currency],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];
  if (rows.length === 0) return <p className="text-center text-slate-500 py-8">No activity in selected period</p>;

  // Cumulative P&L chart
  let cumGgr = 0;
  const cumData = rows.map(r => {
    cumGgr += (r.casino_ggr + r.sport_ggr);
    return { date: r.date, ggr: r.casino_ggr + r.sport_ggr, cumulative_ggr: cumGgr, net_flow: r.deposits - r.withdrawals };
  });

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Cumulative GGR & Net Cash Flow</h2>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={cumData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '11px' }} />
            <YAxis stroke="#9ca3af" tickFormatter={(v: number) => fmtMoney(sym, v)} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number) => fmtMoney(sym, v)} />
            <Legend />
            <Bar dataKey="ggr" name="Daily GGR" fill="#009EA0" radius={[2, 2, 0, 0]} opacity={0.6} />
            <Line dataKey="cumulative_ggr" name="Cumulative GGR" type="monotone" stroke="#009EA0" strokeWidth={2} dot={false} />
            <Line dataKey="net_flow" name="Net Cash Flow" type="monotone" stroke="#8b5cf6" strokeWidth={2} dot={false} strokeDasharray="5 5" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
